import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

/**
 * Auth state lives in:
 *   - The httpOnly `f2_token` cookie (server-managed; the JS world
 *     can't see or mutate it — that's the whole point of the Feb 2026
 *     migration off `localStorage`).
 *   - `f2_user` in `localStorage` — a *display* cache of the latest
 *     `/auth/me` payload. Keeping the user record (name, role) client-
 *     readable lets the app render the shell offline without waiting on
 *     `/auth/me`. The token itself is never stored here anymore.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("f2_user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      // Corrupted JSON in localStorage — clear it so we don't keep
      // throwing on every page load.
      console.warn("[auth] failed to parse cached f2_user, clearing", e);
      try { localStorage.removeItem("f2_user"); } catch (_) { /* ignore */ }
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // The cookie either auths us or it doesn't — there's no JS-visible
    // token to short-circuit on, so just ask the server.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("f2_user", JSON.stringify(data));
    } catch (e) {
      // /auth/me failed — either the cookie is expired/missing (401, the
      // common case) or the server is unreachable. Either way we drop
      // the cached user; log non-401s since those are real bugs.
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[auth] /auth/me failed", e);
      }
      setUser(null);
      localStorage.removeItem("f2_user");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      // The server sets the httpOnly cookie via Set-Cookie on the
      // response. We only persist the user record locally.
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("f2_user", JSON.stringify(data.user));
      setUser(data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const register = async (payload) => {
    // ADMIN-ONLY action: create another user account. The current actor
    // (the admin) keeps their own session — we DO NOT swap `user` /
    // `f2_user` to the newly-created account, and the server no longer
    // sets a cookie for the new user. Doing either would silently
    // hijack the admin's session and the next admin call would 403.
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Server-side cookie clear — the cookie is httpOnly so JS can't
    // delete it without the round trip. Local state still wipes first
    // so the UI flips to logged-out instantly without waiting on the
    // network.
    setUser(null);
    localStorage.removeItem("f2_user");
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Best-effort. If the server is unreachable the cookie still
      // expires on its own (max_age=14d) and the next `/auth/me` 401s.
      console.warn("[auth] logout request failed", e);
    }
  };

  // useMemo on the context value so consumers only re-render when
  // one of the actual fields (user, loading, ...) changes, not on
  // every re-render of AuthProvider. `login`/`register`/`logout`
  // aren't wrapped in useCallback because they're only invoked in
  // event handlers (never passed to useEffect deps). `refresh` is
  // already useCallback-stable so it's safe here.
  const ctxValue = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading, refresh],
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
